import React from 'react';
import { isSameDay, isSameHour, isSameMinute, isSameMonth, isSameYear } from 'date-fns';
import { State } from 'router5';
import { IMeetingDetailsDTO } from '../../api/meetings.dao';
import { IRoomDetailsDTO } from '../../api/rooms.dao';
import { IControlMeetingDetailsDTO, MeetingProgress } from '../../api/control.dao';
import { utcToZonedTime } from '../utils/date-fns-overrides.utils';
import { IRuntimeConfiguration, OEConnectEnvironment } from '../runtime-configuration';
import { MAX_AUDIO_DURATION_SECONDS, MAX_FILE_SIZE, SUPPORTED_AUDIO_TYPES, SUPPORTED_IMAGE_TYPES } from '../../constants/file.constants';
import { IEventTrackerDetailsDTO, ISelectedFilter } from '../../api/events.dao';
import { AudioBridgeProvider, IMeetingConnectionsDTO, IRoomMeetingConnectionsDTO } from '../../api/connections.dao';
import { IOrganizationsForSpeakersSync } from '../../components/_events/_manage-event/shared/connections-list/_connection-form/_sync-speakers/sync-speakers.viewmodel';
import { PARAGRAPHS_REG_EXP } from '../../constants/regex.constants';
import { Container } from 'typescript-ioc';
import { filtersTrackingValuesMapper, TFilterName } from '../../tracking-service/tracking-values';

type IMeetingRoomDetails = IMeetingDetailsDTO | IRoomDetailsDTO | IControlMeetingDetailsDTO;
type TimeGroupItem = { startDateTime: Date; endDateTime?: Date; mergedDateTime?: Date };
export type TPartialProps<T> = Partial<T>;

export const groupListByTime = <T extends TimeGroupItem>(list: T[], equalFn = isItemTimeEqualPrevItemTime): T[][] => {
    const result = [];
    let currentGroup = [];

    (list || []).forEach((item, index, array) => {
        const prevItem = index !== 0 ? array[index - 1] : null;
        if (!prevItem || equalFn(item, prevItem)) {
            currentGroup.push(item);
        } else {
            result.push(currentGroup);
            currentGroup = [item];
        }

        if (array.length - 1 === index) {
            result.push(currentGroup);
        }
    });

    return result;
};

export const groupListByLocalMergedDate = <T extends IEventTrackerDetailsDTO>(list: T[]): T[][] => {
    return groupListByTime(list, isItemLocalMergedDatesEquals);
};

export interface IMeetingsGroup {
    key: string;
    startDateTime: Date;
    endDateTime: Date;
    meetings: IControlMeetingDetailsDTO[];
    hiddenMeetings?: IControlMeetingDetailsDTO[];
}

export const getMeetingGroupsWithHiddenSection = (list: IMeetingRoomDetails[], progressList: MeetingProgress[]): IMeetingsGroup[] => {
    const groupedByTimeMeetings = groupListByTime(list);
    const result = [];

    (groupedByTimeMeetings as IControlMeetingDetailsDTO[][]).forEach((group: IControlMeetingDetailsDTO[]) => {
        const [firstGroupMeeting] = group;
        result.push({
            key: firstGroupMeeting.meetingId,
            startDateTime: firstGroupMeeting.startDateTime,
            endDateTime: firstGroupMeeting.endDateTime,
            meetings: progressList.length ? group.filter(meeting => progressList.includes(meeting.progress)) : group,
            hiddenMeetings: progressList.length ? group.filter(meeting => !progressList.includes(meeting.progress)) : [],
        });
    });
    return result;
};

export const getProgressList = (progress: string): MeetingProgress[] => {
    return progress.length ? progress.split(',') as MeetingProgress[] : [];
};

export const isItemTimeEqualPrevItemTime = (item: TimeGroupItem, prevItem: TimeGroupItem) => {
    return item && isSameHour(item.startDateTime, item.startDateTime)
        && isSameHour(item.endDateTime, prevItem.endDateTime)
        && isSameMinute(item.startDateTime, prevItem.startDateTime)
        && isSameMinute(item.endDateTime, prevItem.endDateTime)
        && isSameDay(item.startDateTime, prevItem.startDateTime);
};

export const isItemLocalDatesEquals = (item: TimeGroupItem, prevItem: TimeGroupItem): boolean => {
    return item && isSameDay(item.startDateTime, prevItem.startDateTime)
        && isSameMonth(item.startDateTime, prevItem.startDateTime)
        && isSameYear(item.startDateTime, prevItem.startDateTime);
};

export const isItemLocalMergedDatesEquals = (item: TimeGroupItem, prevItem: TimeGroupItem): boolean => {
    return item && isSameDay(item.mergedDateTime, prevItem.mergedDateTime)
        && isSameMonth(item.mergedDateTime, prevItem.mergedDateTime)
        && isSameYear(item.mergedDateTime, prevItem.mergedDateTime);
};

export const isDatesEquals = (date1: Date, date2: Date) => {
    return isSameHour(date1, date2)
        && isSameMonth(date1, date2)
        && isSameDay(date1, date2)
        && isSameYear(date1, date2);
};

export const isPreviousGroupDayDifferent = (
    group: IMeetingRoomDetails[],
    prevGroup: IMeetingRoomDetails[],
    timeZone: string
): boolean => {
    const zonedGroupStartDate = utcToZonedTime(group[0].startDateTime, timeZone);
    const zonedPrevGroupStartDate = utcToZonedTime(prevGroup[0].startDateTime, timeZone);

    return isSameDay(zonedGroupStartDate, zonedPrevGroupStartDate);
};

export const isPathIncludesInCurrentRoute = (routePath: string, currentRoute: State) => {
    const { path, params } = currentRoute;
    let currentPath = path;

    Object.keys(params).map(key => {
        currentPath = currentPath.replace(params[key], `:${key}`);
    });

    return currentPath.includes(routePath);
};

export const getEnv = (): OEConnectEnvironment => {
    const config = window['OEConnectConfig'] as { env: OEConnectEnvironment };
    return config.env;
};

export const isProduction = (): boolean => {
    return getEnv() === OEConnectEnvironment.Prod;
};

export const getEnvPostfix = (): string => {
    // @ts-ignore
    switch (window.OEConnectConfig.env) {
        case OEConnectEnvironment.Prod:
            return '.';
        case OEConnectEnvironment.Stage:
            return '.stage-';
        default:
            return '.dev-';
    }
};

export const calcByteCodeSize = (value: string): number => {
    /*
        The function calculate size of byte code. This solution needed for a dynamic validate event description fields [SLEIP-79],
        because mySQL don't write string of size more than 65535 byte and every Cyrillic symbol has size 2 byte.
    */
    return new Blob([value]).size;
};

export const getIsSafariBrowser = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.indexOf('safari') !== -1 && userAgent.indexOf('chrome') === -1;
};

export const getOrderLink = (orderId: string) => {
    return `https://openexchange.lightning.force.com/lightning/r/Order/${orderId}/view`;
};

const escapeRegExp = (expectedString: string) => {
    return expectedString ?
        expectedString.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&') :
        '';
};

export const replaceStringWithHighlight = (checkingValue: string, searchValue: string, stringForRegExp?: string) => {

    if (!searchValue) {
        return checkingValue;
    }

    const formattedSearchWord = escapeRegExp(searchValue.trim());
    const formattedCheckingValue = escapeRegExp(checkingValue);

    if (stringForRegExp) {
        const newResultString = checkingValue.replace(
            new RegExp(stringForRegExp, 'gi'),
            match => `<mark className="highlight-search-result">${match}</mark>`
        );

        return newResultString ? <span dangerouslySetInnerHTML={{ __html: newResultString }} /> : checkingValue;
    } else {
        const resultString = formattedSearchWord
            && formattedCheckingValue.includes(formattedSearchWord)
            && checkingValue.replace(
                new RegExp(formattedSearchWord, 'gi'),
                match => `<mark className="highlight-search-result">${match}</mark>`
            );

        return resultString ? <span dangerouslySetInnerHTML={{ __html: resultString }} /> : checkingValue;
    }
};

export const getRandomPassword = (passwordMinLength = 8): string => {
    let result = '';

    const charSet = 'abcdefghijklmnopqrstuvwxyz';
    const punctuationSet = '\\!@#$%^&*()_+~`|}{[]:;?><,./-=\'\"';
    const numberSet = '0123456789';
    const combinedSet = charSet + charSet.toUpperCase() + punctuationSet + numberSet;

    const addSymbolFromSet = (set: string) => {
        result += set.charAt(Math.floor(Math.random() * set.length));
    };

    for (let i = 0; i < passwordMinLength; i++) {
        addSymbolFromSet(combinedSet);
    }

    [charSet, charSet.toUpperCase(), punctuationSet, numberSet].forEach(set => {
        if (!set.split('').some(el => result.includes(el))) {
            addSymbolFromSet(set);
        }
    });

    return result;
};

// Invoke VisibilitySensor to re-check visible items after changing items height
// We can't use window.dispatchEvent(new Event('scroll')) here, because of IE browser compatibility
export const invokeScrollEvent = () => {
    window.scrollBy(0, 1);
    window.scrollBy(0, -1);
};


export const getOrderBookingFormattedUrl = (accountId: number): string => {
    const runtimeConfiguration = Container.get(IRuntimeConfiguration);

    return 'https://booking.' + runtimeConfiguration.getAppDomain() + `.com/account/${accountId}`;
};

export const getEventHubRootDomain = (): string => {
    switch (Container.get(IRuntimeConfiguration).getEnv()) {
        case OEConnectEnvironment.Prod:
            return '.hub.open-exchange.net';
        case OEConnectEnvironment.Stage:
            return '.hub.stage-open-exchange.net';
        default:
            return '.hub.dev-open-exchange.net';
    }
};

export const getEventHubApiRoot = (): string => {
    switch (Container.get(IRuntimeConfiguration).getEnv()) {
        case OEConnectEnvironment.Prod:
            return 'https://hub-api.open-exchange.net';
        case OEConnectEnvironment.Stage:
            return 'https://hub-api.stage-open-exchange.net';
        default:
            return 'https://hub-api.dev-open-exchange.net';
    }
};

export const getFormattedEventHubUrl = (subdomain: string): string => {
    return 'https://' + subdomain + getEventHubRootDomain();
};

export const getFormattedUrl = (value: string, withoutPostfix?: boolean, accountSubdomain?: string): string => {
    const prefix = 'https://';
    const postfix = withoutPostfix ? '' : 'open-exchange.net/';

    const getPostfix = (): string => {
        // @ts-ignore
        switch (window.OEConnectConfig.env) {
            case OEConnectEnvironment.Prod:
                return '.' + postfix;
            case OEConnectEnvironment.Stage:
                return '.stage-' + postfix;
            default:
                return '.dev-' + postfix;
        }
    };

    if (accountSubdomain) {
        return prefix + accountSubdomain + getPostfix() + value;
    }

    return prefix + value + getPostfix();
};

export const getCompleteRoomName = <T extends { name: string; label?: string }>(room: T) => {
    return room.name + (room.label?.length ? `: ${room.label}` : '');
};

const MP4_AUDIO_BRANDS = ['M4A ', 'M4B '];
const MP4_VIDEO_BRANDS = ['isom', 'iso2', 'iso4', 'iso5', 'iso6', 'mp41', 'mp42', 'avc1', 'dash', 'M4V '];

// Currently just for font types
export const getMimeTypeFromBuffer = (buffer: number[]): { ext: string; mime: string } => {
    const check = (pattern: number[], options?: { offset: number }): boolean => {
        const offset = options?.offset || 0;
        for (let i = 0; i < pattern.length; i++) {
            if (buffer[offset + i] !== pattern[i]) {
                return false;
            }
        }
        return true;
    };

    const checkString = (str: string, options?: { offset: number }): boolean => {
        const offset = options?.offset || 0;
        for (let i = 0; i < str.length; i++) {
            if (buffer[offset + i] !== str.charCodeAt(i)) {
                return false;
            }
        }
        return true;
    };

    if (
        checkString('wOFF') &&
        (
            check([0x00, 0x01, 0x00, 0x00], { offset: 4 }) ||
            checkString('OTTO', { offset: 4 })
        )
    ) {
        return {
            ext: 'woff',
            mime: 'font/woff'
        };
    }

    if (
        checkString('wOF2') &&
        (
            check([0x00, 0x01, 0x00, 0x00], { offset: 4 }) ||
            checkString('OTTO', { offset: 4 })
        )
    ) {
        return {
            ext: 'woff2',
            mime: 'font/woff2'
        };
    }

    if (check([0x00, 0x01, 0x00, 0x00, 0x00])) {
        return {
            ext: 'ttf',
            mime: 'font/ttf',
        };
    }

    if (
        check([0x4C, 0x50], { offset: 34 }) &&
        (
            check([0x00, 0x00, 0x01], { offset: 8 }) ||
            check([0x01, 0x00, 0x02], { offset: 8 }) ||
            check([0x02, 0x00, 0x02], { offset: 8 })
        )
    ) {
        return {
            ext: 'eot',
            mime: 'application/vnd.ms-fontobject'
        };
    }

    if (check([0x4F, 0x54, 0x54, 0x4F, 0x00])) {
        return {
            ext: 'otf',
            mime: 'font/otf',
        };
    }

    if (checkString('RIFF') && checkString('WAVE', { offset: 8 })) {
        return {
            ext: 'wav',
            mime: 'audio/wav'
        };
    }

    if (checkString('fLaC')) {
        return {
            ext: 'flac',
            mime: 'audio/flac'
        };
    }

    if (checkString('OggS')) {
        return {
            ext: 'ogg',
            mime: 'audio/ogg'
        };
    }

    if (check([0x1A, 0x45, 0xDF, 0xA3])) {
        return {
            ext: 'webm',
            mime: 'audio/webm'
        };
    }

    const hasMp4Brand = (brands: string[]): boolean =>
        checkString('ftyp', { offset: 4 }) && brands.some(brand => checkString(brand, { offset: 8 }));

    if (hasMp4Brand(MP4_AUDIO_BRANDS)) {
        return {
            ext: 'm4a',
            mime: 'audio/mp4'
        };
    }

    if (hasMp4Brand(MP4_VIDEO_BRANDS)) {
        return {
            ext: 'mp4',
            mime: 'video/mp4'
        };
    }

    if (buffer[0] === 0xFF && (buffer[1] === 0xF1 || buffer[1] === 0xF9)) {
        return {
            ext: 'aac',
            mime: 'audio/aac'
        };
    }

    if (checkString('ID3') || (buffer[0] === 0xFF && (buffer[1] & 0xE0) === 0xE0)) {
        return {
            ext: 'mp3',
            mime: 'audio/mpeg'
        };
    }

    // Other checks for different file types can go here
    return {
        ext: 'unknown',
        mime: 'unknown'
    };
};

export const isFileFormatValid = async (file: File, exceptSVG?: boolean, supportedTypes = SUPPORTED_IMAGE_TYPES): Promise<boolean> => {
    let type = file.type;
    if (!type) {
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = Array.from(new Uint8Array(arrayBuffer));
        const mimeTypeInfo = getMimeTypeFromBuffer(uint8Array);
        type = mimeTypeInfo.mime;
    }
    if (!file || !supportedTypes.includes(type)) {
        return false;
    } else if (exceptSVG && (!file || !supportedTypes.filter(el => el !== 'image/svg+xml').includes(type))) {
        return false;
    }
    return true;
};

export const isFileSizeValid = (file: File, maxSize = MAX_FILE_SIZE): boolean => {
    if (!file || file.size > maxSize) {
        return false;
    }
    return true;
};

export const getImageDimensions = async (file: File, url?: string): Promise<{ width: number; height: number }> => {
    const URL = window.URL || window.webkitURL;
    const img = new Image();
    if (!url) img.src = URL.createObjectURL(file);
    else img.src = url;
    return await new Promise<{ width: number; height: number }>(resolve => {
        img.onload = () => {
            resolve({ width: img.width, height: img.height });
        };
    });
};

export const checkFileMinWidthHeight = async (file: File, minDimensions: number, url?: string): Promise<boolean> => {
    const imageDimensions = await getImageDimensions(file, url);
    if (imageDimensions.width < minDimensions || imageDimensions.height < minDimensions) {
        return false;
    } else return true;
};

export const checkFileMaxWidthHeight = async (file: File, dimensions: number, url?: string): Promise<boolean> => {
    const imageDimensions = await getImageDimensions(file, url);
    if (imageDimensions.width > dimensions || imageDimensions.height > dimensions) {
        return false;
    } else return true;

};
const AUDIO_HEADER_BYTES = 16;

const readFileHeaderBytes = async (file: File, length: number): Promise<number[]> => {
    return await new Promise<number[]>(resolve => {
        const reader = new FileReader();
        reader.onload = () => resolve(Array.from(new Uint8Array(reader.result as ArrayBuffer)));
        reader.onerror = () => resolve([]);
        reader.readAsArrayBuffer(file.slice(0, length));
    });
};

export const getAudioMimeType = async (file: File, supportedTypes = SUPPORTED_AUDIO_TYPES): Promise<string> => {
    if (!file) {
        return '';
    }
    const { mime } = getMimeTypeFromBuffer(await readFileHeaderBytes(file, AUDIO_HEADER_BYTES));
    return supportedTypes.includes(mime) ? mime : '';
};

const AUDIO_DURATION_TIMEOUT_MS = 3000;

export const getAudioDuration = async (file: File): Promise<number> => {
    const URL = window.URL || window.webkitURL;
    const objectUrl = URL.createObjectURL(file);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.src = objectUrl;
    return await new Promise<number>(resolve => {
        let timeoutId: number;
        const finish = (duration: number) => {
            window.clearTimeout(timeoutId);
            URL.revokeObjectURL(objectUrl);
            resolve(duration);
        };

        timeoutId = window.setTimeout(() => finish(NaN), AUDIO_DURATION_TIMEOUT_MS);

        audio.onloadedmetadata = () => {
            if (audio.duration === Infinity) {
                audio.ontimeupdate = () => {
                    audio.ontimeupdate = null;
                    finish(audio.duration);
                };
                audio.currentTime = Number.MAX_SAFE_INTEGER;
                return;
            }
            finish(audio.duration);
        };
        audio.onerror = () => finish(NaN);
    });
};

export const isAudioDurationValid = async (file: File, maxDuration = MAX_AUDIO_DURATION_SECONDS): Promise<boolean> => {
    const duration = await getAudioDuration(file);
    if (!Number.isFinite(duration)) {
        return true;
    }
    return Math.round(duration) <= maxDuration;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isRegularObject = (value: any) => {
    return value !== null && typeof value === 'object' && value.toString() === '[object Object]';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformObjectToFormDataCompatible = (object: any, parentKey?: string) => {
    let result = {};
    for (const key in object) {
        if (isRegularObject(object[key])) {
            result = { ...result, ...transformObjectToFormDataCompatible(object[key], parentKey ? `${parentKey}.${key}` : key) };
        } else if (object[key] === null || object[key] === undefined) {
            result[key] = '';
        }
        else {
            parentKey ? result[`${parentKey}.${key}`] = object[key] : result[key] = object[key];
        }
    }
    return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isFieldExistInArray(array: Record<string, any>[], key: string, value: string): boolean {
    return array.some(item => item[key] === value);
}

export const getFileType = (filename: string): string => {
    return filename.split('.').pop().toLocaleLowerCase();
};

export const replaceCommaAndSemicolon = (file: File): File => {
    return new File([file], file.name.replace(/,|;/g, ' '), { type: file.type });
};

export const getCommaSeparatedStringList = (value: string): string[] => {
    return value?.length ? value?.split(',').filter(item => !!item.trim()) : [];
};

export const getOrganizationsForSpeakersSync = (
    meetingDetails: IRoomMeetingConnectionsDTO | IControlMeetingDetailsDTO | IMeetingConnectionsDTO
): IOrganizationsForSpeakersSync => {
    return {
        presenters: meetingDetails?.presenters || [],
        hosts: meetingDetails?.hosts || [],
        supports: meetingDetails?.supports || [],
    };
};

export const removeNotParagraphTags = (htmlContent: string): string => {
    // Replace all not paragraph HTML tags with an empty string
    const cleanHtml = htmlContent?.replace(PARAGRAPHS_REG_EXP, '');
    return cleanHtml;
};

export const htmlToPlainText = (html: string): string => {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const nonTextElements = parsedDocument.querySelectorAll('script, style');
    nonTextElements.forEach(element => element.remove());
    return parsedDocument.body.textContent ?? '';
};

export const AudioBridgeProviderLabel = {
    [AudioBridgeProvider.ZOOM]: 'Zoom',
    [AudioBridgeProvider.TURBOBRIDGE]: 'TurboBridge',
    [AudioBridgeProvider.OTHER]: 'Other',
};

export const fetchEnvironment = (): string => {
    // @ts-ignore
    switch (window.OEConnectConfig.env) {
        case OEConnectEnvironment.Prod:
            return 'prod';
        case OEConnectEnvironment.Stage:
            return 'stage';
        default:
            return 'dev';
    }
};

export const getFiltersNames = (selectedFilters: ISelectedFilter[], isFilterBySearchQuery: boolean): string[] => {
    if (!selectedFilters.length && !isFilterBySearchQuery) return [];

    const filters = selectedFilters
        .map(f => filtersTrackingValuesMapper[f.filterName] || f.filterName)
        .filter(f => f !== 'Columns')
    ;
    if (isFilterBySearchQuery) {
        filters.push('Search');
    }
    return filters;
};

export const getListFromSelectedFilters = (selectedFilters: string[]): string => {
    return selectedFilters.join(', ');
};

export const buildSelectedFiltersList = (selectedFilters: string[], selectedFilter?: TFilterName): string => {
    let filteredList = changeDatesTitle(selectedFilters);
    if (selectedFilter) {
        filteredList = excludeSelectedFilter(selectedFilter, filteredList);
    }
    if (!filteredList.length) return 'None';
    return getListFromSelectedFilters(filteredList);
};

const changeDatesTitle = (selectedFilters: string[]): string[] => {
    if (selectedFilters.some(f => f.includes('Date'))) {
        return [
            'Date',
            ...selectedFilters.filter(f => f !== 'Start Date' && f !== 'End Date')
        ];
    }
    return selectedFilters;
};

const excludeSelectedFilter = (selectedFilter: TFilterName, selectedFilters: string[]): string[] => {
    return selectedFilters.filter(f => !f.includes(selectedFilter));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isEmptyArray = (arr: any[]): boolean => arr.length === 0;